// Static fixture-backed data layer for the SME preview build.
//
// The prototype's live backend (Express + SQLite + Google/Microsoft/Merchant APIs) is
// intentionally NOT shipped into tinuiti-frontend. Instead we store a snapshot of real Away
// audit results as JSON on the CDN and serve the exact same API surface the components expect,
// computing audit-to-audit diffs client-side via the ported pure diff functions. Triage is
// persisted to localStorage so reviewers can still triage findings (per-browser; not collected
// centrally). createAudit / deleteAudit are unsupported in this build.

import { computeAuditDiff, decorateWithDiff } from './diff';
import { AuditDetailResponse, AuditDiffResult, AuditRun, Finding, Triage } from './types';

const FIXTURE_CDN_BASE = 'https://cdn.blisspointmedia.com/paid-search-audit/fixtures';
const FIXTURE_IDS = [
  '0a3d373b-1690-4906-a71e-725635776e7c',
  '82926878-cf80-4d52-8a26-1f0e67fa15bd',
  '6ad4ef1a-5490-489a-8df8-c17be9c1c563'
];

const fetchFixture = async (id: string): Promise<AuditDetailResponse> => {
  const res = await fetch(`${FIXTURE_CDN_BASE}/${id}.json`);
  if (!res.ok) throw new Error(`Failed to fetch fixture ${id}: ${res.status}`);
  return res.json() as Promise<AuditDetailResponse>;
};

// The promise is memoised — subsequent calls resolve instantly from the already-loaded data.
let fixturesPromise: Promise<AuditDetailResponse[]> | null = null;

const loadAuditsDesc = (): Promise<AuditDetailResponse[]> => {
  if (!fixturesPromise) {
    fixturesPromise = Promise.all(FIXTURE_IDS.map(fetchFixture)).then(audits =>
      audits.sort((a, b) => (a.run.triggeredAt < b.run.triggeredAt ? 1 : -1))
    );
  }
  return fixturesPromise;
};

// The most-recent bundled audit strictly older than `id` — the implicit diff baseline and the
// "previous audit" pointer, scoped to the bundled set (not the full prototype history).
const priorOf = (audits: AuditDetailResponse[], id: string): AuditDetailResponse | null => {
  const idx = audits.findIndex(a => a.run.id === id);
  if (idx === -1) return null;
  return audits[idx + 1] ?? null;
};

// ----- localStorage-backed triage overlay -----

const TRIAGE_KEY = 'bp-audit-triage';

interface TriageOverride {
  triage: Triage;
  auditorNote: string;
}

// Keyed by `${auditId}:${findingId}`.
type TriageStore = Record<string, TriageOverride>;

const readTriageStore = (): TriageStore => {
  try {
    const raw = localStorage.getItem(TRIAGE_KEY);
    return raw ? (JSON.parse(raw) as TriageStore) : {};
  } catch {
    return {};
  }
};

const applyTriage = (auditId: string, findings: Finding[]): Finding[] => {
  const store = readTriageStore();
  return findings.map(f => {
    const override = store[`${auditId}:${f.id}`];
    return override ? { ...f, triage: override.triage, auditorNote: override.auditorNote } : f;
  });
};

// ----- API surface (mirrors the prototype's axios api.ts) -----

export const listAudits = async (): Promise<AuditRun[]> => {
  const audits = await loadAuditsDesc();
  return audits.map(a => a.run);
};

export const createAudit = (): Promise<AuditRun> =>
  Promise.reject(new Error('Creating new audits is not available in the preview build.'));

export const deleteAudit = (): Promise<void> =>
  Promise.reject(new Error('Deleting audits is not available in the preview build.'));

export const getAudit = async (id: string): Promise<AuditDetailResponse> => {
  const audits = await loadAuditsDesc();
  const raw = audits.find(a => a.run.id === id);
  if (!raw) throw new Error(`Audit ${id} not found in preview fixtures.`);
  const prior = priorOf(audits, id);
  const findings = decorateWithDiff(
    applyTriage(id, raw.findings),
    prior ? applyTriage(prior.run.id, prior.findings) : null
  );
  const previousAudit = prior ? { id: prior.run.id, triggeredAt: prior.run.triggeredAt } : null;
  return { run: raw.run, findings, previousAudit };
};

// Dedicated "What changed" diff. priorId optional → most-recent bundled prior.
export const getAuditDiff = async (id: string, priorId?: string): Promise<AuditDiffResult> => {
  const audits = await loadAuditsDesc();
  const raw = audits.find(a => a.run.id === id);
  if (!raw) throw new Error(`Audit ${id} not found in preview fixtures.`);
  const prior = priorId ? (audits.find(a => a.run.id === priorId) ?? null) : priorOf(audits, id);
  const currentRef = { id: raw.run.id, triggeredAt: raw.run.triggeredAt, title: raw.run.title };
  const priorRef = prior
    ? { id: prior.run.id, triggeredAt: prior.run.triggeredAt, title: prior.run.title }
    : null;
  return computeAuditDiff(
    applyTriage(id, raw.findings),
    prior ? applyTriage(prior.run.id, prior.findings) : [],
    currentRef,
    priorRef
  );
};

export interface UpdateFindingTriageParams {
  auditId: string;
  findingId: string;
  triage: Triage;
  auditorNote: string;
}

export const updateFindingTriage = (params: UpdateFindingTriageParams): Promise<void> => {
  const store = readTriageStore();
  store[`${params.auditId}:${params.findingId}`] = {
    triage: params.triage,
    auditorNote: params.auditorNote
  };
  try {
    localStorage.setItem(TRIAGE_KEY, JSON.stringify(store));
  } catch {
    // localStorage unavailable (e.g. private mode) — triage just won't persist this session.
  }
  return Promise.resolve();
};
